/* eslint-disable @typescript-eslint/no-explicit-any */
import { push } from '@lagunovsky/redux-react-router'
import update from 'immutability-helper'
import { JwtPayload, jwtDecode } from 'jwt-decode'

import { setEmail, setUser } from '../../app-reducer'
import { getUserProfile } from '../../core/services/menu'
import { login as doLogin, logout as doLogout, getUser } from '../services/auth'

// Actions
const START_LOGIN = 'account/login/START_LOGIN'
const COMPLETE_LOGIN = 'account/login/COMPLETE_LOGIN'
const FAIL_LOGIN = 'account/login/FAIL_LOGIN'
const COMPLETE_LOGOUT = 'account/login/COMPLETE_LOGOUT'
const RESET = 'account/login/RESET'

// Types
type User = { [key: string]: any } | undefined

type State = {
  isLoginFailed: boolean
  isLoggingIn: boolean
  isLoggedIn: boolean
  user: User
  error: string
}

type Action =
  | { type: typeof START_LOGIN }
  | { type: typeof COMPLETE_LOGIN; user: User }
  | { type: typeof FAIL_LOGIN; error: string }
  | { type: typeof COMPLETE_LOGOUT }
  | { type: typeof RESET }

// Initial state
const user = getUser()
const initialState: State = {
  isLoginFailed: false,
  isLoggingIn: false,
  isLoggedIn: !!user,
  user,
  error: '',
}

// Reducer
export const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case START_LOGIN:
      return update(state, {
        $merge: {
          isLoginFailed: false,
          isLoggingIn: true,
          isLoggedIn: false,
          error: '',
        },
      })

    case COMPLETE_LOGIN:
      return update(state, {
        $merge: {
          isLoginFailed: false,
          isLoggingIn: false,
          isLoggedIn: true,
          user: action.user,
        },
      })

    case FAIL_LOGIN:
      return update(state, {
        $merge: {
          isLoginFailed: true,
          isLoggingIn: false,
          isLoggedIn: false,
          error: action.error,
        },
      })

    case COMPLETE_LOGOUT:
      return update(state, {
        $merge: {
          isLoginFailed: false,
          isLoggingIn: false,
          isLoggedIn: false,
          user: undefined,
        },
      })

    case RESET:
      return update(state, {
        $merge: {
          isLoginFailed: false,
          isLoggingIn: false,
          isLoggedIn: false,
          error: '',
          user: undefined,
        },
      })

    default:
      return state
  }
}

// Action creators
const startLogin = (): Action => ({
  type: START_LOGIN,
})

const completeLogin = (user: User): Action => ({
  type: COMPLETE_LOGIN,
  user,
})

const failLogin = (error: string): Action => ({
  type: FAIL_LOGIN,
  error,
})

export const login =
  (email: string, password: string, redirect: string) =>
  async (dispatch: any) => {
    dispatch(startLogin())
    try {
      const { phone, token } = await doLogin(email, password)
      dispatch(completeLogin({ phone }))
      const decodedToken: JwtPayload & { email?: string } = jwtDecode(token)
      dispatch(setEmail({ email: decodedToken.email }))
      try {
        const { data, status } = await getUserProfile()
        if (status === 200) {
          dispatch(setUser({ user: data }))
        }
      } catch (profileError) {
        // Keep login successful even if profile fetch fails.
        console.log(profileError)
      }
      dispatch(push(redirect, { from: 'login' }))
    } catch (error: any) {
      return dispatch(failLogin(error?.message))
    }
  }

const completeLogout = (): Action => ({
  type: COMPLETE_LOGOUT,
})

export const logout = () => (dispatch: any) => {
  doLogout()
  dispatch(completeLogout())
  dispatch(setEmail({ email: '' }))
}

export const resetLogin = (): Action => ({
  type: RESET,
})
